import { Box, FormControl, Typography } from '@mui/material'
import {
  ContainerPositionType,
  EquipmentType,
  HoldRestrictionSummaryDto,
  OperationType,
  WorkInstructionWarningReason,
} from '@operations/app/api'
import { IContainerPositionRadioOption } from '@operations/app/models/operator-pages'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { OperatorWithYardSelector } from '../../models/yard-selector.model'
import { JobAlert } from '../JobCard/JobAlert'
import { ContainerPositionRadio } from './ContainerPositionRadio'
import { StepperContentWrapper } from './StepperContentWrapper'

interface Props extends OperatorWithYardSelector {
  currentEquipmentId?: number
  assignedEquipmentId?: number
  hasError?: boolean
  equipmentOptions: IContainerPositionRadioOption[]
  canCurrentEquipmentBeAssignedToNextJob: boolean
  stepsTotal: number
  currentStep: number
  equipmentType: EquipmentType
  orderId?: number | null
  cargoUnitNumber?: string | null
  hasStopped?: boolean
  jobWarningReason?: WorkInstructionWarningReason
  entryHoldRestriction?: HoldRestrictionSummaryDto | null
  exitHoldRestriction?: HoldRestrictionSummaryDto | null
  yardHoldRestriction?: HoldRestrictionSummaryDto | null
  jobOperationType?: OperationType
  containerPosition?: ContainerPositionType
  journeyId?: number | null
  onPositionChange: (hasSlotSelected: boolean) => void
  onBlockingIssueChange: (hasBlockingIssue: boolean) => void
  handleEquipment: (id?: number, containerPosition?: ContainerPositionType | undefined) => void
}

export const SetContainerPosition = observer(
  ({
    currentEquipmentId,
    assignedEquipmentId,
    hasError,
    equipmentOptions,
    canCurrentEquipmentBeAssignedToNextJob,
    stepsTotal,
    currentStep,
    equipmentType,
    orderId,
    cargoUnitNumber,
    hasStopped,
    jobWarningReason,
    entryHoldRestriction,
    exitHoldRestriction,
    yardHoldRestriction,
    jobOperationType,
    containerPosition,
    journeyId,
    yardSelector,
    onPositionChange,
    onBlockingIssueChange,
    handleEquipment,
  }: Props) => {
    const { t } = useTranslate()

    const options = [...equipmentOptions]

    if (canCurrentEquipmentBeAssignedToNextJob) {
      options.push({
        id: currentEquipmentId!,
        type: equipmentType,
        title: t('none', 'None'),
        subtitle: t('useMyEquipment', 'Use my equipment'),
      })
    }

    return (
      <StepperContentWrapper
        title={t('setContainerPosition', 'Set container position')}
        steps={`${currentStep} ${t('of', 'of')} ${stepsTotal}`}
      >
        <Box mb={2}>
          <JobAlert
            warningReason={jobWarningReason}
            isOnStepper
            entryHoldRestriction={entryHoldRestriction}
            exitHoldRestriction={exitHoldRestriction}
            yardHoldRestriction={yardHoldRestriction}
            operationType={jobOperationType}
          />
        </Box>

        {options.length > 0 && (
          <FormControl sx={{ width: '100%' }}>
            <ContainerPositionRadio
              equipmentId={assignedEquipmentId}
              equipments={options}
              hasError={hasError}
              orderId={orderId}
              cargoUnitNumber={cargoUnitNumber}
              hasYardDestination={hasStopped}
              containerPositionType={containerPosition}
              journeyId={journeyId}
              yardSelector={yardSelector}
              onDestinationChange={handleEquipment}
              onPositionChange={onPositionChange}
              onBlockingIssueChange={onBlockingIssueChange}
            />
          </FormControl>
        )}
        {!options.length && !canCurrentEquipmentBeAssignedToNextJob && (
          <Typography fontWeight='bold'>{t('noEquipmentFound', 'No equipment found')}</Typography>
        )}
      </StepperContentWrapper>
    )
  },
)
